import {
  ItemType,
  ArchType,
  ProductPreferenceType,
  ArchTypeTranslated,
  FileType,
  ItemShadeType,
  BridgeType,
  RemovableItemOrderType,
  OrderStatus,
  UserType,
} from "../types/enums";

import {
  Order,
  Product,
  ProductCreator,
  ProductPreferences,
  AnatomyItem,
  File,
  RemovableItem,
} from "../types/interfaces";
import { displayDate } from "./date.utils";

import { isArrayOfInt } from "../utils/others.utils";
import {
  formatChoicesData,
  getTeethsFromLowerArch,
  getTeethsFromUpperArch,
  sortTeethAsc,
} from "./item.utils";
import { translateItemType } from "./translation.utils";

export const getAnatomyItemChoiceWhere = (productType: ItemType) => {
  switch (productType) {
    case ItemType.LAY:
      return {
        lay: {
          equals: true,
        },
      };
    case ItemType.CROWN:
      return {
        crown: {
          equals: true,
        },
      };
    case ItemType.BRIDGE:
      return {
        bridge: {
          equals: true,
        },
      };
    case ItemType.FULL_DENTURE:
    case ItemType.IMMEDIATE_DENTURE:
    case ItemType.FULL_DENTURE_WAX_BITE:
      return {
        fullDenture: {
          equals: true,
        },
      };
    case ItemType.PARTIAL_DENTURE:
      return {
        partialDenture: {
          equals: true,
        },
      };
    case ItemType.VENEER:
      return {
        veneer: {
          equals: true,
        },
      };
    default:
      return {};
  }
};

export const isImplant = (order: {
  products?: Array<{ productType: string; implantItem?: Record<string, any> }>;
}): boolean => {
  return (order?.products ?? []).reduce((acc, product) => {
    if (
      product?.productType?.toUpperCase() === ItemType.IMPLANT.toString() ||
      product?.productType?.toUpperCase() ===
        ItemType.IMPLANT_CROWN.toString() ||
      product?.productType?.toUpperCase() === ItemType.ABUTMENT.toString() ||
      product?.productType?.toUpperCase() ===
        ItemType.CUSTOM_ABUTMENT.toString() ||
      product?.productType?.toUpperCase() ===
        ItemType.SPLINTED_CROWN_IMPLANT.toString() ||
      product?.productType?.toUpperCase() ===
        ItemType.BRIDGE_IMPLANT.toString() ||
      product?.productType?.toUpperCase() ===
        ItemType.BRIDGE_PONTIC.toString() ||
      (product?.implantItem ?? []).length > 0
    ) {
      acc = true;
    }
    return acc;
  }, false);
};

export const isOrderWithNoAttachement = (order: {
  files?: Array<{ fileType: string }>;
}): boolean => {
  return (order?.files ?? []).reduce((acc: boolean, file: File) => {
    if (
      file?.fileType?.toLowerCase().replace("_", "") ===
      FileType.ORDER_FILE?.toLowerCase().replace("_", "")
    ) {
      acc = false;
    }
    return acc;
  }, true);
};

export const isPAC = (order: { products?: Array<{ productType: string }> }) => {
  return (order?.products ?? []).find(
    (product: Product) =>
      product.productType === ItemType.FULL_DENTURE.toString()
  );
};

export const isBridgeOnInlayCore = (
  products: Product[],
  actualProduct?: { productType: ItemType; teeth?: number[] }
): boolean => {
  if (!products || products.length === 0) {
    return false;
  }
  const bridge = products.find(
    product => product.productType === ItemType.BRIDGE
  );
  const inlayCore = products.find(
    product => product.productType === ItemType.INLAY_CORE
  );
  if (!bridge || !inlayCore) {
    return false;
  }
  const bridgeItem = (bridge?.anatomyItem ?? []).find(
    (item: AnatomyItem) => item.itemType === ItemType.BRIDGE
  );
  if (bridgeItem?.itemMaterial?.id) {
    // if bridge workflow is already made and creation was made on two product (first the bridge and second the inlay-core)
    // we want to prevent from having the inlay-core workflow skipped
    return false;
  }
  if (actualProduct) {
    const actualProductTeethIncluded = (actualProduct.teeth ?? []).every(
      teeth => ((bridge?.teeth ?? []).includes(teeth) ? true : false)
    );
    if (!actualProductTeethIncluded) {
      return false;
    }
  }
  return (inlayCore?.teeth ?? []).every(teeth => {
    if (!(bridge?.teeth ?? []).includes(teeth)) {
      return false;
    }
    return true;
  });
};

export const isBridgeAndInlayCoreOverlapping = (
  products: { productType: ItemType; teeth?: number[] }[]
) => {
  let returnVal = false;
  const bridge = products.find(
    product => product.productType === ItemType.BRIDGE
  );
  const inlayCore = products.find(
    product => product.productType === ItemType.INLAY_CORE
  );
  if (!bridge || !inlayCore) {
    return false;
  }
  const bridgeTeeth = bridge.teeth as number[];
  const inlayCoreTeeth = inlayCore.teeth as number[];
  inlayCoreTeeth.forEach(tooth => {
    if (bridgeTeeth.includes(tooth)) {
      returnVal = true;
    }
  });
  return returnVal;
};

export const getAllNonSplintedCrown = (teeths: number[]) => {
  const splintedCrowns: number[][] = getAllPossibleSplintedCrown(teeths);
  return teeths.filter((teeth: number) => {
    return !splintedCrowns.find((splintedCrownOption: number[]) =>
      splintedCrownOption.includes(teeth)
    );
  });
};

export const computeSplintedCrownByArch = (
  archTeeth: number[],
  output: number[][]
) => {
  let actualSplintedCrown = [];
  let i = 0;
  const teethUniversal = convertTeethNumberFromFdiToUniversalFormat(
    archTeeth
  ).sort((a, b) => a - b);
  while (i < teethUniversal.length - 1) {
    if (teethUniversal[i] - teethUniversal[i + 1] === -1) {
      while (teethUniversal[i] - teethUniversal[i + 1] === -1) {
        if (!actualSplintedCrown.includes(teethUniversal[i])) {
          actualSplintedCrown.push(teethUniversal[i]);
        }
        if (!actualSplintedCrown.includes(teethUniversal[i + 1])) {
          actualSplintedCrown.push(teethUniversal[i + 1]);
        }
        i++;
      }
      output.push(actualSplintedCrown);
      actualSplintedCrown = [];
    } else {
      i++;
    }
  }
  return output;
};

export const getAllPossibleSplintedCrown = (teeth: number[] = []) => {
  let output = [];
  const lowerTeeth = getTeethsFromLowerArch(teeth ?? []);
  const upperTeeth = getTeethsFromUpperArch(teeth ?? []);

  if (lowerTeeth.length > 0) {
    output = computeSplintedCrownByArch(lowerTeeth, output);
  }
  if (upperTeeth.length > 0) {
    output = computeSplintedCrownByArch(upperTeeth, output);
  }
  return output.map((teeth: number[]) =>
    convertTeethNumberFromUniversalToFdiFormat(teeth)
  );
};

export const convertTeethNumberFromFdiToUniversalFormat = (
  teeth: number[]
): number[] | null => {
  return teeth.reduce((acc: number[], teeth: number) => {
    const allFormatForTeeth = teethFormat.find(format => format.fdi === teeth);
    if (allFormatForTeeth?.universal) {
      acc.push(allFormatForTeeth.universal);
    }
    return acc;
  }, []);
};

export const convertTeethNumberFromUniversalToFdiFormat = (
  teeth: number[]
): number[] | null => {
  return teeth.reduce((acc: number[], teeth: number) => {
    const allFormatForTeeth = teethFormat.find(
      format => format.universal === teeth
    );
    if (allFormatForTeeth?.fdi) {
      acc.push(allFormatForTeeth.fdi);
    }
    return acc;
  }, []);
};

const teethFormat: { fdi: number; universal: number }[] = [
  {
    fdi: 11,
    universal: 8,
  },
  {
    fdi: 12,
    universal: 7,
  },
  {
    fdi: 13,
    universal: 6,
  },
  {
    fdi: 14,
    universal: 5,
  },
  {
    fdi: 15,
    universal: 4,
  },
  {
    fdi: 16,
    universal: 3,
  },
  {
    fdi: 17,
    universal: 2,
  },
  {
    fdi: 18,
    universal: 1,
  },
  {
    fdi: 21,
    universal: 9,
  },
  {
    fdi: 22,
    universal: 10,
  },
  {
    fdi: 23,
    universal: 11,
  },
  {
    fdi: 24,
    universal: 12,
  },
  {
    fdi: 25,
    universal: 13,
  },
  {
    fdi: 26,
    universal: 14,
  },
  {
    fdi: 27,
    universal: 15,
  },
  {
    fdi: 28,
    universal: 16,
  },
  {
    fdi: 38,
    universal: 17,
  },
  {
    fdi: 37,
    universal: 18,
  },
  {
    fdi: 36,
    universal: 19,
  },
  {
    fdi: 35,
    universal: 20,
  },
  {
    fdi: 34,
    universal: 21,
  },
  {
    fdi: 33,
    universal: 22,
  },
  {
    fdi: 32,
    universal: 23,
  },
  {
    fdi: 31,
    universal: 24,
  },
  {
    fdi: 48,
    universal: 32,
  },
  {
    fdi: 47,
    universal: 31,
  },
  {
    fdi: 46,
    universal: 30,
  },
  {
    fdi: 45,
    universal: 29,
  },
  {
    fdi: 44,
    universal: 28,
  },
  {
    fdi: 43,
    universal: 27,
  },
  {
    fdi: 42,
    universal: 26,
  },
  {
    fdi: 41,
    universal: 25,
  },
];

export const removeTeethFromBridgeOnInlayCore = (
  crownTeeths: number[],
  products: { productType: ItemType; teeth?: number[] }[]
) => {
  const allBridges =
    products.filter(product => product.productType === ItemType.BRIDGE) ?? [];
  allBridges.forEach(bridge => {
    crownTeeths = crownTeeths.filter(teeth => !bridge.teeth?.includes(teeth));
  });
  return crownTeeths;
};

export const getAnatomyItemMaterialChoices = (
  anatomyItemChoicesData: {
    getItemMaterialsWhere: { id: string; inlayCore: string; crown: string }[];
  },
  itemType: ItemType
) => {
  let filteredAnatomyItemChoicesData =
    anatomyItemChoicesData.getItemMaterialsWhere;

  if (itemType === ItemType.INLAY_CORE) {
    filteredAnatomyItemChoicesData = filteredAnatomyItemChoicesData.filter(
      m => m.crown || m.id === "METAL_NON_PRECIOUS"
    );
  }

  return formatChoicesData(filteredAnatomyItemChoicesData);
};

export const getProductItem = (product: Product): any => {
  if (isAnatomyItemType(product.productType) && product.anatomyItem) {
    return product.anatomyItem[0];
  } else if (isImplantItemType(product.productType) && product.implantItem) {
    return product.implantItem[0];
  } else if (
    isRemovableItemType(product.productType) &&
    product.removableItem
  ) {
    return product.removableItem[0];
  }
  return null;
};

export const getBridgeTypeLabel = (product: Product) => {
  if (product.productType === ItemType.BRIDGE && product?.anatomyItem) {
    if (product.anatomyItem[0].bridgeType === BridgeType.CONVENTIONAL) {
      return "Conventionnel";
    } else if (product.anatomyItem[0].bridgeType === BridgeType.CANTILEVER) {
      return "Cantilever";
    } else if (product.anatomyItem[0].bridgeType === BridgeType.MARYLAND) {
      return "Maryland";
    }
  }
  return "";
};

export const getItemTypeLabelPlural = (
  productType: ItemType,
  teeth: number[]
) => {
  const plural = teeth.length > 1 ? true : false;
  if (
    (!plural && productType !== ItemType.IMPLANT) ||
    isRemovableItemType(productType) ||
    productType === ItemType.BRIDGE ||
    productType === ItemType.BRIDGE_IMPLANT
  ) {
    return getItemTypeLabel(productType);
  }
  if (productType === ItemType.CROWN) {
    return "Couronnes";
  } else if (productType === ItemType.SPLINTED_CROWN) {
    return "Couronnes solidarisées";
  } else if (productType === ItemType.INLAY) {
    return "Inlays";
  } else if (
    productType === ItemType.IMPLANT ||
    productType === ItemType.IMPLANT_CROWN
  ) {
    return "Couronnes sur implant";
  } else if (productType === ItemType.SPLINTED_CROWN_IMPLANT) {
    return "Couronnes solidarisées sur implant";
  } else if (productType === ItemType.ONLAY) {
    return "Onlays";
  } else if (productType === ItemType.LAY) {
    return "Inlays/Onlays";
  } else if (productType === ItemType.VENEER) {
    return "Facettes";
  } else if (productType === ItemType.INLAY_CORE) {
    return "Inlay-cores";
  } else if (productType === ItemType.POST_AND_CORE_RETAINED_CROWN) {
    return "Couronnes Richmond";
  }
};

export const getProductOrderedCountLabel = (product: Product) => {
  if (product === undefined || product.productType === undefined) {
    return "";
  }
  let teethLength =
    product.productType === ItemType.BRIDGE_IMPLANT
      ? 1
      : getProductTeeth(product).length;
  if (
    isAnatomyItemType(product.productType) &&
    product.productType !== ItemType.BRIDGE &&
    product.anatomyItem
  ) {
    teethLength =
      product.anatomyItem.length > 1 ? product.anatomyItem.length : teethLength;
    return `- ${teethLength} x ${getItemTypeLabelPlural(product.productType, getProductTeeth(product))}`;
  } else if (product.productType === ItemType.BRIDGE) {
    const bridgeType = getBridgeTypeLabel(product);
    return `- 1 x ${getItemTypeLabel(product.productType)}${bridgeType ? " " + bridgeType : ""}`;
  } else if (isImplantItemType(product.productType)) {
    return `- ${teethLength} x ${getItemTypeLabelPlural(product.productType, getProductTeeth(product))}`;
  } else if (isRemovableItemType(product.productType)) {
    return `- ${getItemTypeLabel(product.productType)}`;
  }
};

export const getProductsOrderedCountLabel = (order: Order): string => {
  if (!order?.products || order?.products?.length <= 0) {
    return "";
  }
  let productsOrderedCountLabel = "";
  for (let i = 0; i < order?.products?.length; i++) {
    const product = order?.products?.[i];
    if (product) {
      if (
        product.productType === ItemType.INLAY_CORE &&
        order?.products?.length === 1
      ) {
        return (product?.anatomyItem ?? []).length > 1
          ? "Multiples produits"
          : `${(product?.anatomyItem ?? []).length} Inlay-core`;
      } else {
        productsOrderedCountLabel += getProductOrderedCountLabel(product)
          ? `${getProductOrderedCountLabel(product)?.slice(2)}, `
          : "";
      }
    }
  }
  return productsOrderedCountLabel.slice(0, -2);
};

export const getItemTypeLabel = (productType: ItemType) => {
  if (productType === ItemType.FULL_DENTURE) {
    return "Prothèse amovible complète";
  } else if (productType === ItemType.IMMEDIATE_DENTURE) {
    return "Prothèse amovible immédiate";
  } else if (productType === ItemType.FULL_DENTURE_WAX_BITE) {
    return "Cire d'occlusion pour prothèse amovible complète";
  } else if (productType === ItemType.BLEACHING_TRAY) {
    return "Gouttière de blanchiment";
  } else if (productType === ItemType.RETAINER) {
    return "Gouttière de contention";
  } else if (productType === ItemType.NIGHT_GUARD) {
    return "Gouttière bruxisme";
  } else if (productType === ItemType.CROWN) {
    return "Couronne";
  } else if (productType === ItemType.SPLINTED_CROWN) {
    return "Couronne solidarisée";
  } else if (productType === ItemType.INLAY) {
    return "Inlay";
  } else if (productType === ItemType.ONLAY) {
    return "Onlay";
  } else if (productType === ItemType.OVERLAY) {
    return "Overlay";
  } else if (productType === ItemType.LAY) {
    return "Inlay/Onlay/Overlay";
  } else if (productType === ItemType.VENEER) {
    return "Facette";
  } else if (productType === ItemType.INLAY_CORE) {
    return "Inlay-core";
  } else if (
    productType === ItemType.POST_AND_CORE_RETAINED_CROWN ||
    productType === ItemType.RICHMOND_CROWN
  ) {
    return "Couronne Richmond";
  } else if (productType === ItemType.BRIDGE) {
    return "Bridge";
  } else if (productType === ItemType.IMPLANT) {
    return "Pilier";
  } else if (productType === ItemType.IMPLANT_CROWN) {
    return "Couronne sur implant";
  } else if (productType === ItemType.SPLINTED_CROWN_IMPLANT) {
    return "Couronne solidarisée sur implant";
  } else if (productType === ItemType.SPLINT) {
    return "Gouttière";
  } else if (productType === ItemType.PARTIAL_DENTURE) {
    return "Prothèse amovible partielle";
  } else if (productType === ItemType.BRIDGE_IMPLANT) {
    return "Bridge sur implant";
  } else if (productType === ItemType.ABUTMENT) {
    return "Pilier standard";
  } else if (productType === ItemType.SCAN_ONLY) {
    return "Autres produits";
  } else if (productType === ItemType.SPACE_MAINTAINER) {
    return "Mainteneur d'espace";
  } else if (productType === ItemType.RETENTION_WIRE) {
    return "Fil de contention";
  } else if (productType === ItemType.WAX_UP) {
    return "Wax-up";
  } else if (productType === ItemType.CUSTOM_TRAY) {
    return "Porte-empreinte individuel";
  } else if (
    productType === ItemType.ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS
  ) {
    return "Empreinte supplémentaire pour prothèse amovible";
  } else if (productType === ItemType.ADDING_TEETH_ON_EXISTING_DENTURE) {
    return "Adjonction de dents sur prothèse existante";
  } else if (productType === ItemType.DENTURE_REPAIR) {
    return "Réparation de prothèse amovible";
  } else if (productType === ItemType.ORTHO_MODEL) {
    return "Modèle d'étude orthodontique";
  } else if (productType === ItemType.WAX_BITE) {
    return "Cire d'occlusion";
  } else {
    return "non défini";
  }
};

export const isAnatomyItemType = (productType: ItemType) => {
  return (
    productType === ItemType.CROWN ||
    productType === ItemType.SPLINTED_CROWN ||
    productType === ItemType.LAY ||
    productType === ItemType.INLAY ||
    productType === ItemType.ONLAY ||
    productType === ItemType.OVERLAY ||
    productType === ItemType.VENEER ||
    productType === ItemType.INLAY_CORE ||
    productType === ItemType.POST_AND_CORE_RETAINED_CROWN ||
    productType === ItemType.RICHMOND_CROWN ||
    productType === ItemType.BRIDGE
  );
};

export const isImplantItemType = (productType: ItemType) => {
  return (
    productType === ItemType.IMPLANT ||
    productType === ItemType.ABUTMENT ||
    productType === ItemType.CUSTOM_ABUTMENT ||
    productType === ItemType.IMPLANT_CROWN ||
    productType === ItemType.SPLINTED_CROWN_IMPLANT ||
    productType === ItemType.BRIDGE_IMPLANT
  );
};

export const isRemovableItemType = (productType: ItemType) => {
  return (
    productType === ItemType.PARTIAL_DENTURE ||
    productType === ItemType.FULL_DENTURE ||
    productType === ItemType.IMMEDIATE_DENTURE ||
    productType === ItemType.FULL_DENTURE_WAX_BITE ||
    productType === ItemType.SPLINT ||
    productType === ItemType.BLEACHING_TRAY ||
    productType === ItemType.RETAINER ||
    productType === ItemType.NIGHT_GUARD ||
    productType === ItemType.RETENTION_WIRE ||
    productType === ItemType.SPACE_MAINTAINER ||
    productType === ItemType.ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS ||
    productType === ItemType.WAX_UP ||
    productType === ItemType.CUSTOM_TRAY ||
    productType === ItemType.ADDING_TEETH_ON_EXISTING_DENTURE ||
    productType === ItemType.DENTURE_REPAIR ||
    productType === ItemType.ORTHO_MODEL ||
    productType === ItemType.WAX_BITE
  );
};

export const getArchFromTeeth = (
  teeths: number[],
  shouldTranslate: boolean = false
): string => {
  if (!Array.isArray(teeths) || teeths.length === 0) {
    return "";
  }
  const onlyMaxilaire = teeths.every(teeth => teeth <= 28);
  const onlyMandibulaire = teeths.every(teeth => teeth >= 31);
  if (onlyMaxilaire && !onlyMandibulaire) {
    return shouldTranslate
      ? ArchTypeTranslated.MAXILLAIRE.toString()
      : ArchType.MAXILLAIRE.toString();
  }
  if (onlyMandibulaire && !onlyMaxilaire) {
    return shouldTranslate
      ? ArchTypeTranslated.MANDIBULAIRE.toString()
      : ArchType.MANDIBULAIRE.toString();
  }
  return shouldTranslate
    ? ArchTypeTranslated.BIMAXILAIRE.toString()
    : ArchType.BIMAXILAIRE.toString();
};

export const getRemovableItemFromArch = (removableItem: RemovableItem[]) => {
  return {
    mandibularTeeth: removableItem.find(
      item => getTeethsFromLowerArch(item.teeth).length > 0
    ),
    maxillaryTeeth: removableItem.find(
      item => getTeethsFromUpperArch(item.teeth).length > 0
    ),
  };
};

export const getTeethsByArch = (teeths: number[]) => {
  const mandibularTeeth = getTeethsFromLowerArch(teeths);
  const maxillaryTeeth = getTeethsFromUpperArch(teeths);
  return {
    mandibularTeeth,
    maxillaryTeeth,
  };
};
export const getMandibularTeethForPartialDentureInfo = (
  missingTeeth: number[],
  toIncludeTeeth: number[]
) => {
  return {
    missingTeeth: sortTeethAsc(getTeethsFromLowerArch(missingTeeth)),
    toIncludeTeeth: sortTeethAsc(getTeethsFromLowerArch(toIncludeTeeth)),
  };
};

export const getMaxillaryTeethForPartialDentureInfo = (
  missingTeeth: number[],
  toIncludeTeeth: number[]
) => {
  return {
    missingTeeth: sortTeethAsc(getTeethsFromUpperArch(missingTeeth)),
    toIncludeTeeth: sortTeethAsc(getTeethsFromUpperArch(toIncludeTeeth)),
  };
};

export const getProductTeeth = (product: Product): number[] => {
  const productType = product?.productType;

  if (productType === ItemType.PARTIAL_DENTURE) {
    return product.removableItem.reduce((acc, removableItem) => {
      acc.push(...removableItem.teeth);
      return acc;
    }, []);
  }
  if (
    productType === ItemType.BRIDGE_IMPLANT ||
    productType === ItemType.IMPLANT
  ) {
    return product.teeth;
  } else if (
    isAnatomyItemType(productType) &&
    product.anatomyItem &&
    product.anatomyItem.length > 0 &&
    product.anatomyItem[0].teeth
  ) {
    return product.teeth ? (product.teeth as number[]) : [];
  } else if (
    isImplantItemType(productType) &&
    product.implantItem &&
    product.implantItem.length > 0 &&
    product.implantItem[0].teeth
  ) {
    return product.implantItem[0].teeth;
  } else if (
    isRemovableItemType(productType) &&
    product.removableItem &&
    product.removableItem.length > 0 &&
    product.removableItem[0].teeth
  ) {
    return product.removableItem[0].teeth;
  }
  return [];
};

export const getTeethLabel = (
  itemType: ItemType,
  teeth: number[],
  teethToManufacture?: number[],
  shouldTranslate: boolean = false
): string => {
  if (!isArrayOfInt(teeth)) return "";
  const isWithArch = isRemovableItemType(itemType);
  let teethLabel = "";
  if (teeth.length <= 0) {
    return "non définies";
  }
  if (isWithArch === false) {
    teethLabel = teeth.length > 1 ? "dents " : "dent ";
    [...(teeth as number[])]
      .sort((a, b) => a - b)
      .forEach(tooth => {
        if (!isNaN(tooth)) {
          teethLabel += tooth + " ";
        }
      });
    if (teethLabel) {
      teethLabel = teethLabel.slice(0, -1);
    }
  } else {
    if (
      itemType !== ItemType.FULL_DENTURE &&
      itemType !== ItemType.FULL_DENTURE_WAX_BITE &&
      teethToManufacture &&
      teethToManufacture.length > 0
    ) {
      teeth = teethToManufacture ?? teeth;
    }
    if (itemType === ItemType.PARTIAL_DENTURE) {
      return `${getArchFromTeeth(teeth, shouldTranslate)}: ${teeth.join(", ")}`;
    }
    return getArchFromTeeth(teeth, shouldTranslate);
  }
  if (itemType === ItemType.BRIDGE_IMPLANT) {
    return teeth.join(" ");
  }
  return teethLabel;
};

export const getAllTeeths = (): number[] => {
  const arr = [];
  for (let i = 11; i <= 18; i++) {
    arr.push(i);
  }
  for (let i = 21; i <= 28; i++) {
    arr.push(i);
  }
  for (let i = 31; i <= 38; i++) {
    arr.push(i);
  }
  for (let i = 41; i <= 48; i++) {
    arr.push(i);
  }
  return arr;
};

export const findUserPreference = (
  userId: string,
  productPreferences: ProductPreferences[]
) => {
  return (productPreferences ?? []).find(
    preference =>
      preference?.dentist?.id === userId &&
      preference?.productPreferenceType ===
        ProductPreferenceType.FIXED_PROSTHESIS_PREFERENCES
  );
};

export const findUserPreferenceForRemovablePreference = (
  userId: string,
  productPreferences: ProductPreferences[]
) => {
  return (productPreferences ?? []).find(
    preference =>
      preference?.dentist?.id === userId &&
      preference?.productPreferenceType ===
        ProductPreferenceType.REMOVABLE_PROSTHESIS_PREFERENCES
  );
};

export const getGingivaShadeFormChoices = (removableItemChoicesData: {
  getItemShadesWhere: {
    id: string;
    label: string;
    shadeType: string;
    gingiva: boolean;
  }[];
}) => {
  return removableItemChoicesData?.getItemShadesWhere
    ? formatChoicesData(
        removableItemChoicesData.getItemShadesWhere.filter(
          shade => shade.gingiva === true
        )
      )
    : [];
};

export const getShadeTypeFormChoices = (removableItemChoicesData: {
  getItemShadesWhere?: {
    id: string;
    label: string;
    shadeType: string;
    gingiva: boolean;
  }[];
}) => {
  return (removableItemChoicesData?.getItemShadesWhere || [])
    .map((item: any) => ({
      label: item.shadeType.toString().replaceAll("_", " "),
      value: item.shadeType,
      shadeType: item.shadeType,
    }))
    .reduce((acc, item) => {
      if (
        !acc.some(i => i.value === item.value) &&
        item.shadeType !== ItemShadeType.GINGIVA
      ) {
        acc.push(item);
      }
      return acc;
    }, []);
};

export const getPartialDentureTeeth = (product: Product): number[] => {
  if (product?.productType !== ItemType.PARTIAL_DENTURE) {
    return [];
  }
  const selectedTeeth = [];
  if (product.removableItem?.[0]?.teeth) {
    selectedTeeth.push(...product.removableItem?.[0]?.teeth);
  }
  if (product.removableItem?.[1]?.teeth) {
    selectedTeeth.push(...product.removableItem?.[1]?.teeth);
  }
  return selectedTeeth;
};

export const getTeethNumbers = (selectedProduct: Product) => {
  if (
    selectedProduct?.productType === ItemType.PARTIAL_DENTURE &&
    selectedProduct?.removableItem?.length > 0
  ) {
    const papTeeth = getPartialDentureTeeth(selectedProduct);
    return papTeeth.length > 0 ? papTeeth : selectedProduct?.teeth;
  }

  if (
    selectedProduct?.removableItem?.length > 0 &&
    selectedProduct?.removableItem[0]?.isBothJaws
  ) {
    return getAllTeeths();
  }

  return selectedProduct?.teeth;
};

const shouldShowFullDentureRdv2Option = (order?: Order): boolean => {
  if (!order?.products) return false;

  if (order.status !== OrderStatus.DELIVERED) return false;

  const fullDentureProducts = order.products.filter(
    (p: Product) => p.productType === ItemType.FULL_DENTURE
  );

  if (fullDentureProducts.length === 0) return false;

  const hasFinal = fullDentureProducts.some(
    (p: Product) =>
      p.removableItem?.[0]?.fullDentureOrderType ===
      RemovableItemOrderType.FINAL
  );

  if (hasFinal) return false;

  return fullDentureProducts.some((p: Product) => {
    return (
      p.removableItem?.[0]?.fullDentureOrderType ===
        RemovableItemOrderType.WAX_BITE ||
      p.removableItem?.[0]?.fullDentureOrderType ===
        RemovableItemOrderType.TRY_IN
    );
  });
};

const shouldShowPartialDentureRdv2Option = (order?: Order): boolean => {
  if (!order?.products) return false;
  if (order.status !== OrderStatus.DELIVERED) return false;

  const partialDentureProducts = order.products.filter(
    (p: Product) => p.productType === ItemType.PARTIAL_DENTURE
  );

  if (partialDentureProducts.length === 0) return false;

  const hasFinal = partialDentureProducts.some(
    (p: Product) =>
      p.removableItem?.[0]?.partialDentureOrderType ===
      RemovableItemOrderType.FINAL
  );

  if (hasFinal) return false;

  return partialDentureProducts.some((p: Product) => {
    return (
      p.removableItem?.[0]?.partialDentureOrderType ===
        RemovableItemOrderType.WAX_BITE ||
      p.removableItem?.[0]?.partialDentureOrderType ===
        RemovableItemOrderType.TRY_IN
    );
  });
};

export const getAddProductToOrderFormItemTypeChoices = (
  language: "FR" | "EN" = "FR",
  options: { order?: Order; fromLabs?: boolean } = {}
) => {
  const getLabel = (itemType: ItemType) => {
    return language === "FR"
      ? getItemTypeLabel(itemType)
      : translateItemType(itemType);
  };

  const groupLabels = {
    FR: {
      fixed: "Fixe",
      removable: "Amovible",
      implant: "Implant",
      misc: "Divers",
    },
    EN: {
      fixed: "Fixed",
      removable: "Removable",
      implant: "Implant",
      misc: "Miscellaneous",
    },
  };

  return [
    {
      group: groupLabels[language].fixed,
      items: [
        { value: ItemType.INLAY_CORE, label: getLabel(ItemType.INLAY_CORE) },
        { value: ItemType.CROWN, label: getLabel(ItemType.CROWN) },
        { value: ItemType.LAY, label: getLabel(ItemType.LAY) },
        { value: ItemType.VENEER, label: getLabel(ItemType.VENEER) },
        { value: ItemType.BRIDGE, label: getLabel(ItemType.BRIDGE) },
      ],
    },
    {
      group: groupLabels[language].removable,
      items: [
        {
          value: ItemType.PARTIAL_DENTURE,
          label: getLabel(ItemType.PARTIAL_DENTURE),
        },
        {
          value: ItemType.FULL_DENTURE,
          label: getLabel(ItemType.FULL_DENTURE),
        },
        ...(options.fromLabs || shouldShowFullDentureRdv2Option(options?.order)
          ? [
              {
                value: FULL_DENTURE_RDV2_CONTINUATION,
                label:
                  language === "FR"
                    ? "Prothèse amovible complète RDV2+"
                    : "Full Denture RDV2+",
              },
            ]
          : []),
        ...(options.fromLabs ||
        shouldShowPartialDentureRdv2Option(options?.order)
          ? [
              {
                value: PARTIAL_DENTURE_RDV2_CONTINUATION,
                label:
                  language === "FR"
                    ? "Prothèse amovible partielle RDV2+"
                    : "Partial Denture RDV2+",
              },
            ]
          : []),
        {
          value: ItemType.SPLINT,
          label: getLabel(ItemType.SPLINT),
        },
      ],
    },
    {
      group: groupLabels[language].implant,
      items: [
        {
          value: ItemType.BRIDGE_IMPLANT,
          label: getLabel(ItemType.BRIDGE_IMPLANT),
        },
        {
          value: ItemType.IMPLANT_CROWN,
          label: getLabel(ItemType.IMPLANT_CROWN),
        },
      ],
    },
    {
      group: groupLabels[language].misc,
      items: [
        { value: ItemType.SCAN_ONLY, label: getLabel(ItemType.SCAN_ONLY) },
      ],
    },
  ];
};

export const AddProductToOrderFormItemTypeChoices =
  getAddProductToOrderFormItemTypeChoices("FR");

export const filterProductsForRdv2Display = <
  T extends { removableItem?: { rdvNumber?: number | null }[] },
>(
  products: T[],
  isRdv2Continuation: boolean
): T[] => {
  if (!isRdv2Continuation) {
    return products;
  }

  const maxRdvNumber = Math.max(
    ...products.map(p => p.removableItem?.[0]?.rdvNumber ?? 0)
  );

  return products.filter(p => {
    const rdvNumber = p.removableItem?.[0]?.rdvNumber;
    return rdvNumber === maxRdvNumber;
  });
};

export type Rdv2PreviousProducts = Partial<Record<string, Product>>;

const getDentureOrderType = (product: Product) =>
  product.productType === ItemType.FULL_DENTURE
    ? product.removableItem?.[0]?.fullDentureOrderType
    : product.removableItem?.[0]?.partialDentureOrderType;

const isAwaitingRdv2Continuation = (
  order: Order,
  productType: ItemType.FULL_DENTURE | ItemType.PARTIAL_DENTURE
): boolean => {
  const orderTypes = (order.products ?? [])
    .filter(product => product.productType === productType)
    .map(getDentureOrderType);

  return (
    !orderTypes.includes(RemovableItemOrderType.FINAL) &&
    (orderTypes.includes(RemovableItemOrderType.WAX_BITE) ||
      orderTypes.includes(RemovableItemOrderType.TRY_IN))
  );
};

export const getRdv2PreviousProducts = (order: Order): Rdv2PreviousProducts => {
  const previousProducts: Rdv2PreviousProducts = {};

  for (const productType of [
    ItemType.FULL_DENTURE,
    ItemType.PARTIAL_DENTURE,
  ] as const) {
    const previousProduct = order.products?.findLast(
      product => product.productType === productType
    );
    if (previousProduct && isAwaitingRdv2Continuation(order, productType)) {
      previousProducts[productType] = previousProduct;
    }
  }

  return previousProducts;
};

export const FULL_DENTURE_RDV2_CONTINUATION = "FULL_DENTURE_RDV2_CONTINUATION";

export const PARTIAL_DENTURE_RDV2_CONTINUATION =
  "PARTIAL_DENTURE_RDV2_CONTINUATION";

export const getProductCreatorLabel = (
  createdBy: ProductCreator | null | undefined,
  shouldTranslate: boolean
): string | null => {
  if (!createdBy) return null;

  if (createdBy.type === UserType.JASPER) {
    return shouldTranslate
      ? `${createdBy.firstName} from Jasper`
      : `${createdBy.firstName} de Jasper`;
  }

  return shouldTranslate ? "Dentist" : "le dentiste";
};

export const getProductCreationLabel = (
  product: Pick<Product, "createdAt" | "createdBy">,
  {
    shouldTranslate,
    withCreator = false,
  }: { shouldTranslate: boolean; withCreator?: boolean }
): string | null => {
  if (!product.createdAt) return null;

  const date = displayDate(new Date(product.createdAt), false);
  const creator = withCreator
    ? getProductCreatorLabel(product.createdBy, shouldTranslate)
    : null;

  if (!creator) {
    return shouldTranslate ? `Added on : ${date}` : `Ajouté le : ${date}`;
  }

  return shouldTranslate
    ? `Added on ${date} by ${creator}`
    : `Ajouté le ${date} par ${creator}`;
};
